<template>
  <div class="relative h-full lg:mt-0 -mt-4 bg-gray-50 min-h-screen">
    <nav-menu />

    <!-- Mobile Reminders Panel (Top of page on mobile) -->
    <div class="lg:hidden">
      <!-- Today/Tomorrow/All Toggle -->
      <div class="bg-white border-b border-gray-200 px-4 py-3">
        <div class="flex items-center gap-2 mb-3">
          <button @click="reminderFilter = 'today'" class="flex-1 h-12 rounded-xl text-base font-bold transition-all" :class="reminderFilter === 'today' ? 'bg-primary text-white shadow-md' : 'bg-gray-100 text-gray-600'">
            <i class="fa-solid fa-calendar-day mr-1.5"></i>Today
          </button>
          <button @click="reminderFilter = 'tomorrow'" class="flex-1 h-12 rounded-xl text-base font-bold transition-all" :class="reminderFilter === 'tomorrow' ? 'bg-primary text-white shadow-md' : 'bg-gray-100 text-gray-600'">
            <i class="fa-solid fa-calendar-plus mr-1.5"></i>Tomorrow
          </button>
          <button @click="reminderFilter = 'all'" class="flex-1 h-12 rounded-xl text-base font-bold transition-all" :class="reminderFilter === 'all' ? 'bg-primary text-white shadow-md' : 'bg-gray-100 text-gray-600'">
            <i class="fa-solid fa-calendar mr-1.5"></i>All
          </button>
        </div>
        <!-- Mobile reminder list -->
        <div v-if="filteredReminders.length" class="space-y-2" :class="{ 'max-h-48 overflow-y-auto': reminderFilter !== 'all' }">
          <div v-for="rem in filteredReminders" :key="rem.id" @click="openReminderLead(rem)" class="flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md" :class="isPastDate(rem.date) ? 'border-red-300 bg-red-50 overdue-reminder-pulse' : 'border-yellow-200 bg-yellow-50'">
            <i class="fa-solid fa-bell text-lg mt-0.5" :class="isPastDate(rem.date) ? 'text-red-500 animate-pulse' : 'text-yellow-500'"></i>
            <div class="flex-1 min-w-0">
              <p class="font-semibold text-gray-900 text-base truncate">{{ rem.subject }}</p>
              <p class="text-sm text-gray-500 truncate">{{ rem.lead ? rem.lead.contact : rem.leadName }}</p>
              <p class="text-xs mt-0.5" :class="isPastDate(rem.date) ? 'text-red-600 font-bold' : 'text-gray-400'"><span v-if="isPastDate(rem.date)" class="inline-flex items-center text-[9px] font-bold bg-red-600 text-white px-1.5 py-0.5 rounded mr-1 uppercase">Overdue</span>{{ formatReminderDate(rem.date) }}</p>
            </div>
            <button @click.stop="confirmRemoveReminder(rem)" class="w-10 h-10 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-100 flex-shrink-0">
              <i class="fa-solid fa-xmark text-base"></i>
            </button>
          </div>
        </div>
        <!-- Clear for the day -->
        <div v-else class="flex flex-col items-center justify-center gap-1 py-5 text-gray-500">
          <i class="fa-solid fa-beer-mug-empty text-yellow-500 text-3xl mb-1"></i>
          <span class="font-bold text-base">{{ reminderFilter === 'today' ? "You're clear for today!" : reminderFilter === 'tomorrow' ? 'Nothing tomorrow!' : 'No reminders!' }}</span>
          <span class="text-xs text-gray-400">No follow-ups needed</span>
        </div>
      </div>
    </div>

    <!-- Header Bar -->
    <div class="w-full bg-white border-b border-gray-200 shadow-sm">
      <div class="px-4 sm:px-6 py-3">
        <!-- Top row: title + actions -->
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-3">
            <h1 class="text-lg font-bold text-gray-900 tracking-tight">CRM</h1>
            <span class="hidden sm:inline-flex text-xs text-gray-400 font-medium bg-gray-100 px-2 py-0.5 rounded-full">{{ leads.length }} leads</span>
          </div>
          <div class="flex items-center gap-2">
            <div class="hidden sm:block flex-shrink-0" style="max-width:160px">
              <outlook />
            </div>
            <button @click="exportLeadsCsv" class="hidden sm:inline-flex items-center px-4 rounded-lg text-sm font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors h-9 sm:h-10" title="Export all visible leads as CSV">
              <i class="fa-solid fa-arrow-up-from-bracket mr-1.5"></i>Export
            </button>
            <button @click="openModal(null)" class="inline-flex items-center px-5 rounded-xl text-base sm:text-sm font-semibold bg-primary text-white hover:opacity-90 transition-colors h-12 sm:h-10">
              <i class="fa-solid fa-plus mr-2 text-lg sm:text-sm"></i><span class="hidden sm:inline">New Lead</span><span class="sm:hidden">Add Lead</span>
            </button>
          </div>
        </div>
        <!-- Filter row -->
        <div class="flex flex-wrap items-center gap-2">
          <div class="inline-flex items-center bg-gray-50 border border-gray-200 rounded-xl flex-1 sm:flex-none sm:w-56 h-12 sm:h-10">
            <i class="fa-solid fa-magnifying-glass text-gray-400 text-base sm:text-xs pl-3 flex-shrink-0"></i>
            <input v-model="searchQuery" type="text" placeholder="Search clients, contacts..." class="bg-transparent text-base sm:text-sm pl-2.5 pr-3 outline-none w-full h-full" />
          </div>
          <select v-model="filterLeads" class="bg-white border border-gray-200 rounded-xl text-base sm:text-sm px-3 outline-none h-12 sm:h-10">
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="quoted">Quoted</option>
            <option value="follow_up">Follow Up</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </select>
          <select v-model="filterRegion" class="bg-white border border-gray-200 rounded-xl text-base sm:text-sm px-3 outline-none h-12 sm:h-10">
            <option value="all">All Regions</option>
            <option v-for="r in availableRegions" :key="r.id" :value="r.name">{{ r.name }}</option>
          </select>
          <select v-model="filterUrgency" class="bg-white border border-gray-200 rounded-xl text-sm px-3 outline-none hidden sm:block h-10">
            <option value="all">All Urgency</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select v-model="filterCustomer" class="bg-white border border-gray-200 rounded-xl text-sm px-3 outline-none hidden sm:block h-10">
            <option value="all">All Customers</option>
            <option v-for="c in uniqueCustomers" :key="c" :value="c">{{ c }}</option>
          </select>
          <label class="hidden sm:inline-flex items-center gap-2 text-sm text-gray-500 whitespace-nowrap cursor-pointer select-none" style="height:36px" @click.prevent="showCompleted = !showCompleted">
            <span class="relative inline-flex items-center flex-shrink-0 rounded-full transition-colors duration-200" :class="showCompleted ? 'bg-primary' : 'bg-gray-300'" style="width:36px;height:20px">
              <span class="inline-block bg-white rounded-full shadow transition-transform duration-200" style="width:16px;height:16px" :style="{ transform: showCompleted ? 'translateX(18px)' : 'translateX(2px)' }"></span>
            </span>
            Completed
          </label>
          <div class="ml-auto flex items-center bg-gray-100 rounded-xl p-0.5 h-12 sm:h-10">
            <button @click="setView('table')" :class="currentView === 'table' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'" class="px-3 sm:px-2.5 h-full rounded-lg text-sm sm:text-xs font-medium transition-all flex items-center gap-1.5">
              <i class="fa-solid fa-table-list text-base sm:text-xs"></i><span class="hidden sm:inline">Table</span>
            </button>
            <button @click="setView('cards')" :class="currentView === 'cards' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'" class="px-3 sm:px-2.5 h-full rounded-lg text-sm sm:text-xs font-medium transition-all flex items-center gap-1.5">
              <i class="fa-solid fa-grip text-base sm:text-xs"></i><span class="hidden sm:inline">Cards</span>
            </button>
            <button @click="setView('trello')" :class="currentView === 'trello' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'" class="px-3 sm:px-2.5 h-full rounded-lg text-sm sm:text-xs font-medium transition-all flex items-center gap-1.5">
              <i class="fa-solid fa-columns text-base sm:text-xs"></i><span class="hidden sm:inline">Board</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- KPI Strip -->
    <div class="px-4 sm:px-6 py-3">
      <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        <div @click="filterLeads = 'all'" class="bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md" :class="filterLeads === 'all' ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200'">
          <p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Total</p>
          <p class="text-xl font-bold text-gray-900 mt-0.5">{{ totalLeadCount }}</p>
        </div>
        <div @click="filterLeads = filterLeads === 'pending' ? 'all' : 'pending'" class="bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md" :class="filterLeads === 'pending' ? 'border-yellow-500 ring-1 ring-yellow-500' : 'border-gray-200'">
          <p class="text-[10px] font-semibold text-yellow-500 uppercase tracking-wider">Pending</p>
          <p class="text-xl font-bold text-yellow-600 mt-0.5">{{ pendingLeadCount }}</p>
        </div>
        <div @click="filterLeads = filterLeads === 'quoted' ? 'all' : 'quoted'" class="bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md" :class="filterLeads === 'quoted' ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200'">
          <p class="text-[10px] font-semibold text-blue-500 uppercase tracking-wider">Quoted</p>
          <p class="text-xl font-bold text-blue-600 mt-0.5">{{ quotedLeadCount }}</p>
        </div>
        <div @click="filterLeads = filterLeads === 'follow_up' ? 'all' : 'follow_up'" class="bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md" :class="filterLeads === 'follow_up' ? 'border-purple-500 ring-1 ring-purple-500' : 'border-gray-200'">
          <p class="text-[10px] font-semibold text-purple-500 uppercase tracking-wider">Follow Up</p>
          <p class="text-xl font-bold text-purple-600 mt-0.5">{{ followUpLeadCount }}</p>
        </div>
        <div @click="filterLeads = filterLeads === 'won' ? 'all' : 'won'" class="bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md" :class="filterLeads === 'won' ? 'border-green-500 ring-1 ring-green-500' : 'border-gray-200'">
          <p class="text-[10px] font-semibold text-green-500 uppercase tracking-wider">Won</p>
          <p class="text-xl font-bold text-green-600 mt-0.5">{{ wonLeadCount }}</p>
        </div>
        <div @click="filterLeads = filterLeads === 'lost' ? 'all' : 'lost'" class="bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md" :class="filterLeads === 'lost' ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200'">
          <p class="text-[10px] font-semibold text-red-500 uppercase tracking-wider">Lost</p>
          <p class="text-xl font-bold text-red-600 mt-0.5">{{ lostLeadCount }}</p>
        </div>
        <div class="bg-white rounded-xl border border-gray-200 p-4 hidden sm:block">
          <p class="text-[10px] font-semibold text-orange-500 uppercase tracking-wider">Hot Leads</p>
          <p class="text-xl font-bold text-orange-600 mt-0.5">{{ hotLeadCount }}</p>
        </div>
        <div class="bg-white rounded-xl border border-gray-200 p-4 hidden sm:block">
          <p class="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Total M3</p>
          <p class="text-xl font-bold text-gray-900 mt-0.5">{{ totalMeters.toLocaleString() }}</p>
        </div>
      </div>
    </div>

    <!-- Expandable Charts Dashboard -->
    <div class="px-4 sm:px-6 pb-3">
      <button @click="toggleCharts" class="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors">
        <i class="fa-solid fa-chart-pie text-xs"></i>
        <span>Dashboard</span>
        <i :class="showCharts ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" class="text-[10px] ml-0.5"></i>
      </button>
      <div v-if="showCharts" class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div class="bg-white rounded-xl border border-gray-200 p-4">
          <h4 class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Lead Status</h4>
          <div v-if="chartsLoading" class="flex items-center justify-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-primary"></div>
          </div>
          <apexchart v-else type="donut" height="240" :options="donutOptions" :series="donutSeries" />
        </div>
        <div class="bg-white rounded-xl border border-gray-200 p-4">
          <h4 class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Leads Over Time</h4>
          <div v-if="chartsLoading" class="flex items-center justify-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-primary"></div>
          </div>
          <apexchart v-else type="area" height="240" :options="areaOptions" :series="areaSeries" />
        </div>
      </div>
    </div>

    <!-- Main content area -->
    <div class="flex w-full" style="min-height: calc(100vh - 280px)">
      <div class="flex-1 min-w-0 px-4 sm:px-6 pb-6">

        <!-- TABLE VIEW -->
        <div v-if="currentView === 'table'" class="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-gray-200 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                  <th class="py-3 px-4 cursor-pointer select-none hover:text-gray-600" @click="setSort('customer')">
                    Customer <i v-if="sortColumn === 'customer'" :class="sortOrder === 'asc' ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'" class="ml-0.5"></i>
                  </th>
                  <th class="py-3 px-4 cursor-pointer select-none hover:text-gray-600" @click="setSort('contact')">
                    Contact <i v-if="sortColumn === 'contact'" :class="sortOrder === 'asc' ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'" class="ml-0.5"></i>
                  </th>
                  <th class="py-3 px-4 cursor-pointer select-none hover:text-gray-600" @click="setSort('region')">
                    Region <i v-if="sortColumn === 'region'" :class="sortOrder === 'asc' ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'" class="ml-0.5"></i>
                  </th>
                  <th class="py-3 px-4 cursor-pointer select-none hover:text-gray-600" @click="setSort('urgency')">
                    Urgency <i v-if="sortColumn === 'urgency'" :class="sortOrder === 'asc' ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'" class="ml-0.5"></i>
                  </th>
                  <th class="py-3 px-4 cursor-pointer select-none hover:text-gray-600" @click="setSort('flames')">
                    Flames <i v-if="sortColumn === 'flames'" :class="sortOrder === 'asc' ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'" class="ml-0.5"></i>
                  </th>
                  <th class="py-3 px-4 cursor-pointer select-none hover:text-gray-600" @click="setSort('assigned')">
                    Assigned <i v-if="sortColumn === 'assigned'" :class="sortOrder === 'asc' ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'" class="ml-0.5"></i>
                  </th>
                  <th class="py-3 px-4 cursor-pointer select-none hover:text-gray-600" @click="setSort('status')">
                    Status <i v-if="sortColumn === 'status'" :class="sortOrder === 'asc' ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'" class="ml-0.5"></i>
                  </th>
                  <th class="py-3 px-4 cursor-pointer select-none hover:text-gray-600 whitespace-nowrap" @click="setSort('jobType')">
                    Job Type <i v-if="sortColumn === 'jobType'" :class="sortOrder === 'asc' ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'" class="ml-0.5"></i>
                  </th>
                  <th class="py-3 px-4 cursor-pointer select-none hover:text-gray-600 whitespace-nowrap" @click="setSort('startDate')">
                    Est Start <i v-if="sortColumn === 'startDate'" :class="sortOrder === 'asc' ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'" class="ml-0.5"></i>
                  </th>
                  <th class="py-3 px-4 cursor-pointer select-none hover:text-gray-600" @click="setSort('m3')">
                    M3 <i v-if="sortColumn === 'm3'" :class="sortOrder === 'asc' ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'" class="ml-0.5"></i>
                  </th>
                  <th class="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="lead in searchFilteredLeads" :key="lead.id" class="border-b border-gray-50 hover:bg-blue-50/30 transition-colors cursor-pointer" :class="hasOverdueReminder(lead) ? 'overdue-pulse bg-red-50/50' : ''" @click="openTaskDetail(lead)">
                  <td class="py-3 px-4">
                    <div class="font-semibold text-gray-900 text-sm">{{ lead.customer ? lead.customer.company : '--' }}</div>
                    <div class="text-xs text-gray-400">{{ lead.client }}</div>
                    <span v-if="hasOverdueReminder(lead)" class="inline-flex items-center text-[9px] font-bold bg-red-600 text-white px-1.5 py-0.5 rounded mt-0.5 uppercase animate-pulse">Overdue</span>
                  </td>
                  <td class="py-3 px-4">
                    <div class="text-sm text-gray-800">{{ lead.contact }}</div>
                    <a v-if="lead.phone" :href="'tel:' + lead.phone" @click.stop class="text-xs text-blue-500 hover:underline">{{ lead.phone }}</a>
                  </td>
                  <td class="py-3 px-4">
                    <span v-if="lead.region" class="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{{ lead.region }}</span>
                  </td>
                  <td class="py-3 px-4">
                    <span class="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full" :class="urgencyClass(lead.urgency)">{{ (lead.urgency || 'low') }}</span>
                  </td>
                  <td class="py-3 px-4">
                    <div class="flex items-center gap-0.5">
                      <i v-for="n in 5" :key="n" class="fa-solid fa-fire text-xs" :style="flameStyle(n, lead.flames || 0)"></i>
                    </div>
                  </td>
                  <td class="py-3 px-4">
                    <span class="text-xs text-gray-600">{{ lead.assignedTo ? lead.assignedTo.name : '--' }}</span>
                  </td>
                  <td class="py-3 px-4" @click.stop>
                    <select v-model="lead.status" @change="handleStatusChange(lead)" class="rounded-lg text-xs font-semibold px-2 py-1 border outline-none" :class="statusSelectClass(lead.status)">
                      <option value="pending">Pending</option>
                      <option value="quoted">Quoted</option>
                      <option value="follow_up">Follow Up</option>
                      <option value="won">Won</option>
                      <option value="lost">Lost</option>
                    </select>
                  </td>
                  <td class="py-3 px-4" @click.stop>
                    <select v-model="lead.jobType" @change="saveFieldInline(lead, 'jobType')" class="rounded-lg text-xs font-semibold px-2 py-1 border outline-none bg-blue-50 text-blue-700 border-blue-200">
                      <option value="">-- Select --</option>
                      <option value="Concrete Testing">Concrete Testing</option>
                      <option value="Soil Testing">Soil Testing</option>
                      <option value="Concrete Scanning & Coring">Concrete Scanning & Coring</option>
                      <option value="Investigation Work">Investigation Work</option>
                      <option value="Package">Package</option>
                    </select>
                  </td>
                  <td class="py-3 px-4 text-xs text-gray-500 whitespace-nowrap">{{ formatDate(lead.estimatedStartDate) }}</td>
                  <td class="py-3 px-4 text-sm text-gray-600 font-medium">{{ lead.approxM3 || '--' }}</td>
                  <td class="py-3 px-4" @click.stop>
                    <div class="flex items-center justify-center gap-1">
                      <button @click="openModal(lead)" class="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Edit lead">
                        <i class="fa-solid fa-pen text-sm"></i>
                      </button>
                      <button @click="openReminderModal(lead)" class="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 transition-colors" title="Set reminder">
                        <i class="fa-solid fa-bell text-sm"></i>
                      </button>
                      <button @click="openAddProjectModal(lead)" class="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors" title="Convert to project">
                        <i class="fa-solid fa-folder-plus text-sm"></i>
                      </button>
                      <button @click="confirmRemoveLead(lead)" class="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Delete lead">
                        <i class="fa-solid fa-trash text-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!searchFilteredLeads.length">
                  <td colspan="11" class="py-16 text-center">
                    <i class="fa-solid fa-inbox text-3xl text-gray-300 mb-3 block"></i>
                    <p class="text-sm text-gray-400">No leads found matching your filters</p>
                    <p class="text-xs text-gray-300 mt-1">Try adjusting your search or filters</p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- CARD GRID VIEW (alphabetical) -->
        <div v-if="currentView === 'cards'">
          <div v-for="group in groupedByCustomer" :key="group.customerName" class="mb-6">
            <div class="flex items-center gap-2 mb-3">
              <h3 class="text-sm font-bold text-gray-800">{{ group.customerName }}</h3>
              <span class="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{{ group.leads.length }}</span>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              <div v-for="lead in group.leads" :key="lead.id" @click="openTaskDetail(lead)" class="rounded-xl border p-4 hover:shadow-md transition-all cursor-pointer relative" :class="hasOverdueReminder(lead) ? 'overdue-pulse bg-red-50 border-red-300' : 'bg-white border-gray-200 hover:border-gray-300'">
                <div class="flex items-center justify-between mb-3">
                  <div class="flex items-center gap-1.5">
                    <span class="text-xs font-semibold px-2 py-0.5 rounded-full" :class="statusPillClass(lead.status)">{{ lead.status }}</span>
                    <span v-if="hasOverdueReminder(lead)" class="text-[9px] font-bold bg-red-600 text-white px-1.5 py-0.5 rounded-full uppercase animate-pulse">Overdue</span>
                  </div>
                  <div class="flex items-center gap-0.5">
                    <i v-for="n in 5" :key="n" class="fa-solid fa-fire" style="font-size:10px" :style="flameStyle(n, lead.flames || 0)"></i>
                  </div>
                </div>
                <h4 class="font-semibold text-gray-900 text-sm mb-1 truncate">{{ lead.projectName || (lead.customer ? lead.customer.company : 'Untitled') }}</h4>
                <div class="space-y-1 text-xs text-gray-500 mb-3">
                  <p v-if="lead.jobType"><i class="fa-solid fa-briefcase w-4 text-blue-400"></i> {{ lead.jobType }}</p>
                  <p v-if="lead.contact"><i class="fa-solid fa-user w-4 text-gray-400"></i> {{ lead.contact }}</p>
                  <p v-if="lead.phone"><i class="fa-solid fa-phone w-4 text-gray-400"></i> <a :href="'tel:'+lead.phone" @click.stop class="text-blue-500">{{ lead.phone }}</a></p>
                  <p v-if="lead.region"><i class="fa-solid fa-map-pin w-4 text-gray-400"></i> {{ lead.region }}</p>
                </div>
                <div class="flex items-center justify-between pt-2 border-t border-gray-100">
                  <span class="text-xs font-semibold px-1.5 py-0.5 rounded" :class="urgencyClass(lead.urgency)">{{ (lead.urgency || 'low') }}</span>
                  <span v-if="lead.assignedTo" class="text-xs text-gray-400"><i class="fa-solid fa-user-check mr-1"></i>{{ lead.assignedTo.name }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!searchFilteredLeads.length" class="py-16 text-center">
            <i class="fa-solid fa-inbox text-3xl text-gray-300 mb-3 block"></i>
            <p class="text-sm text-gray-400">No leads found</p>
          </div>
        </div>

        <!-- TRELLO / BOARD VIEW with drag-and-drop -->
        <div v-if="currentView === 'trello'" class="flex gap-4 overflow-x-auto pb-4" style="min-height: 500px">
          <!-- Pending Column -->
          <div class="flex-shrink-0 w-80 bg-yellow-50/50 rounded-xl border border-yellow-200/50 p-3 flex flex-col">
            <div class="flex items-center justify-between mb-3 px-1">
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-yellow-500"></span>
                <h3 class="text-sm font-bold text-gray-800">Pending</h3>
              </div>
              <span class="text-xs font-medium text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded-full">{{ pendingLeads.length }}</span>
            </div>
            <draggable v-model="pendingLeadsDraggable" group="leads" @end="onDragEnd($event, 'pending')" class="space-y-2 flex-1 min-h-[60px]" ghost-class="opacity-30" drag-class="shadow-lg">
              <div v-for="lead in pendingLeadsDraggable" :key="lead.id" @click="openTaskDetail(lead)" class="rounded-lg border p-3 hover:shadow-md transition-all cursor-pointer" :class="hasOverdueReminder(lead) ? 'overdue-pulse bg-red-50 border-red-300' : 'bg-white border-gray-200'">
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center gap-1">
                    <div class="flex items-center gap-0.5">
                      <i v-for="n in 5" :key="n" class="fa-solid fa-fire" style="font-size:9px" :style="flameStyle(n, lead.flames || 0)"></i>
                    </div>
                    <span v-if="hasOverdueReminder(lead)" class="text-[8px] font-bold bg-red-600 text-white px-1 py-0.5 rounded uppercase animate-pulse">Overdue</span>
                  </div>
                  <span class="text-xs font-semibold px-1.5 py-0.5 rounded" :class="urgencyClass(lead.urgency)">{{ (lead.urgency || 'low') }}</span>
                </div>
                <h4 class="font-semibold text-gray-900 text-sm mb-1 truncate">{{ lead.customer ? lead.customer.company : (lead.projectName || '--') }}</h4>
                <p v-if="lead.jobType" class="text-xs text-yellow-600 truncate mb-0.5"><i class="fa-solid fa-briefcase mr-1 text-yellow-500"></i>{{ lead.jobType }}</p>
                <p v-if="lead.contact" class="text-xs text-gray-500 truncate"><i class="fa-solid fa-user mr-1 text-gray-400"></i>{{ lead.contact }}</p>
                <p v-if="lead.region" class="text-xs text-gray-400 mt-0.5"><i class="fa-solid fa-map-pin mr-1"></i>{{ lead.region }}</p>
                <div v-if="lead.assignedTo" class="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-400">
                  <i class="fa-solid fa-user-check mr-1"></i>{{ lead.assignedTo.name }}
                </div>
              </div>
            </draggable>
          </div>
          <!-- Quoted Column -->
          <div class="flex-shrink-0 w-80 bg-blue-50/50 rounded-xl border border-blue-200/50 p-3 flex flex-col">
            <div class="flex items-center justify-between mb-3 px-1">
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                <h3 class="text-sm font-bold text-gray-800">Quoted</h3>
              </div>
              <span class="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">{{ quotedLeads.length }}</span>
            </div>
            <draggable v-model="quotedLeadsDraggable" group="leads" @end="onDragEnd($event, 'quoted')" class="space-y-2 flex-1 min-h-[60px]" ghost-class="opacity-30" drag-class="shadow-lg">
              <div v-for="lead in quotedLeadsDraggable" :key="lead.id" @click="openTaskDetail(lead)" class="rounded-lg border p-3 hover:shadow-md transition-all cursor-pointer" :class="hasOverdueReminder(lead) ? 'overdue-pulse bg-red-50 border-red-300' : 'bg-white border-gray-200'">
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center gap-1">
                    <div class="flex items-center gap-0.5">
                      <i v-for="n in 5" :key="n" class="fa-solid fa-fire" style="font-size:9px" :style="flameStyle(n, lead.flames || 0)"></i>
                    </div>
                    <span v-if="hasOverdueReminder(lead)" class="text-[8px] font-bold bg-red-600 text-white px-1 py-0.5 rounded uppercase animate-pulse">Overdue</span>
                  </div>
                  <span class="text-xs font-semibold px-1.5 py-0.5 rounded" :class="urgencyClass(lead.urgency)">{{ (lead.urgency || 'low') }}</span>
                </div>
                <h4 class="font-semibold text-gray-900 text-sm mb-1 truncate">{{ lead.customer ? lead.customer.company : (lead.projectName || '--') }}</h4>
                <p v-if="lead.jobType" class="text-xs text-blue-500 truncate mb-0.5"><i class="fa-solid fa-briefcase mr-1 text-blue-400"></i>{{ lead.jobType }}</p>
                <p v-if="lead.contact" class="text-xs text-gray-500 truncate"><i class="fa-solid fa-user mr-1 text-gray-400"></i>{{ lead.contact }}</p>
                <p v-if="lead.region" class="text-xs text-gray-400 mt-0.5"><i class="fa-solid fa-map-pin mr-1"></i>{{ lead.region }}</p>
                <div v-if="lead.assignedTo" class="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-400">
                  <i class="fa-solid fa-user-check mr-1"></i>{{ lead.assignedTo.name }}
                </div>
              </div>
            </draggable>
          </div>
          <!-- Follow Up Column -->
          <div class="flex-shrink-0 w-80 bg-purple-50/50 rounded-xl border border-purple-200/50 p-3 flex flex-col">
            <div class="flex items-center justify-between mb-3 px-1">
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-purple-500"></span>
                <h3 class="text-sm font-bold text-gray-800">Follow Up</h3>
              </div>
              <span class="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">{{ followUpLeads.length }}</span>
            </div>
            <draggable v-model="followUpLeadsDraggable" group="leads" @end="onDragEnd($event, 'follow_up')" class="space-y-2 flex-1 min-h-[60px]" ghost-class="opacity-30" drag-class="shadow-lg">
              <div v-for="lead in followUpLeadsDraggable" :key="lead.id" @click="openTaskDetail(lead)" class="rounded-lg border p-3 hover:shadow-md transition-all cursor-pointer" :class="hasOverdueReminder(lead) ? 'overdue-pulse bg-red-50 border-red-300' : 'bg-white border-gray-200'">
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center gap-1">
                    <div class="flex items-center gap-0.5">
                      <i v-for="n in 5" :key="n" class="fa-solid fa-fire" style="font-size:9px" :style="flameStyle(n, lead.flames || 0)"></i>
                    </div>
                    <span v-if="hasOverdueReminder(lead)" class="text-[8px] font-bold bg-red-600 text-white px-1 py-0.5 rounded uppercase animate-pulse">Overdue</span>
                  </div>
                  <span class="text-xs font-semibold px-1.5 py-0.5 rounded" :class="urgencyClass(lead.urgency)">{{ (lead.urgency || 'low') }}</span>
                </div>
                <h4 class="font-semibold text-gray-900 text-sm mb-1 truncate">{{ lead.customer ? lead.customer.company : (lead.projectName || '--') }}</h4>
                <p v-if="lead.jobType" class="text-xs text-purple-500 truncate mb-0.5"><i class="fa-solid fa-briefcase mr-1 text-purple-400"></i>{{ lead.jobType }}</p>
                <p v-if="lead.contact" class="text-xs text-gray-500 truncate"><i class="fa-solid fa-user mr-1 text-gray-400"></i>{{ lead.contact }}</p>
                <p v-if="lead.region" class="text-xs text-gray-400 mt-0.5"><i class="fa-solid fa-map-pin mr-1"></i>{{ lead.region }}</p>
                <div v-if="lead.assignedTo" class="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-400">
                  <i class="fa-solid fa-user-check mr-1"></i>{{ lead.assignedTo.name }}
                </div>
              </div>
            </draggable>
          </div>
          <!-- Won Column -->
          <div class="flex-shrink-0 w-80 bg-green-50/50 rounded-xl border border-green-200/50 p-3 flex flex-col">
            <div class="flex items-center justify-between mb-3 px-1">
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                <h3 class="text-sm font-bold text-gray-800">Won</h3>
              </div>
              <span class="text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded-full">{{ wonLeads.length }}</span>
            </div>
            <draggable v-model="wonLeadsDraggable" group="leads" @end="onDragEnd($event, 'won')" class="space-y-2 flex-1 min-h-[60px]" ghost-class="opacity-30" drag-class="shadow-lg">
              <div v-for="lead in wonLeadsDraggable" :key="lead.id" @click="openTaskDetail(lead)" class="rounded-lg border p-3 hover:shadow-md transition-all cursor-pointer" :class="hasOverdueReminder(lead) ? 'overdue-pulse bg-red-50 border-red-300' : 'bg-white border-gray-200'">
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center gap-1">
                    <div class="flex items-center gap-0.5">
                      <i v-for="n in 5" :key="n" class="fa-solid fa-fire" style="font-size:9px" :style="flameStyle(n, lead.flames || 0)"></i>
                    </div>
                    <span v-if="hasOverdueReminder(lead)" class="text-[8px] font-bold bg-red-600 text-white px-1 py-0.5 rounded uppercase animate-pulse">Overdue</span>
                  </div>
                  <span class="text-xs font-semibold px-1.5 py-0.5 rounded" :class="urgencyClass(lead.urgency)">{{ (lead.urgency || 'low') }}</span>
                </div>
                <h4 class="font-semibold text-gray-900 text-sm mb-1 truncate">{{ lead.customer ? lead.customer.company : (lead.projectName || '--') }}</h4>
                <p v-if="lead.jobType" class="text-xs text-green-600 truncate mb-0.5"><i class="fa-solid fa-briefcase mr-1 text-green-500"></i>{{ lead.jobType }}</p>
                <p v-if="lead.contact" class="text-xs text-gray-500 truncate"><i class="fa-solid fa-user mr-1 text-gray-400"></i>{{ lead.contact }}</p>
                <p v-if="lead.region" class="text-xs text-gray-400 mt-0.5"><i class="fa-solid fa-map-pin mr-1"></i>{{ lead.region }}</p>
                <div v-if="lead.assignedTo" class="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-400">
                  <i class="fa-solid fa-user-check mr-1"></i>{{ lead.assignedTo.name }}
                </div>
              </div>
            </draggable>
          </div>
          <!-- Lost Column -->
          <div class="flex-shrink-0 w-80 bg-red-50/50 rounded-xl border border-red-200/50 p-3 flex flex-col">
            <div class="flex items-center justify-between mb-3 px-1">
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                <h3 class="text-sm font-bold text-gray-800">Lost</h3>
              </div>
              <span class="text-xs font-medium text-red-600 bg-red-100 px-2 py-0.5 rounded-full">{{ lostLeads.length }}</span>
            </div>
            <draggable v-model="lostLeadsDraggable" group="leads" @end="onDragEnd($event, 'lost')" class="space-y-2 flex-1 min-h-[60px]" ghost-class="opacity-30" drag-class="shadow-lg">
              <div v-for="lead in lostLeadsDraggable" :key="lead.id" @click="openTaskDetail(lead)" class="rounded-lg border p-3 hover:shadow-md transition-all cursor-pointer" :class="hasOverdueReminder(lead) ? 'overdue-pulse bg-red-50 border-red-300' : 'bg-white border-gray-200'">
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center gap-1">
                    <div class="flex items-center gap-0.5">
                      <i v-for="n in 5" :key="n" class="fa-solid fa-fire" style="font-size:9px" :style="flameStyle(n, lead.flames || 0)"></i>
                    </div>
                    <span v-if="hasOverdueReminder(lead)" class="text-[8px] font-bold bg-red-600 text-white px-1 py-0.5 rounded uppercase animate-pulse">Overdue</span>
                  </div>
                  <span class="text-xs font-semibold px-1.5 py-0.5 rounded" :class="urgencyClass(lead.urgency)">{{ (lead.urgency || 'low') }}</span>
                </div>
                <h4 class="font-semibold text-gray-900 text-sm mb-1 truncate">{{ lead.customer ? lead.customer.company : (lead.projectName || '--') }}</h4>
                <p v-if="lead.jobType" class="text-xs text-red-500 truncate mb-0.5"><i class="fa-solid fa-briefcase mr-1 text-red-400"></i>{{ lead.jobType }}</p>
                <p v-if="lead.contact" class="text-xs text-gray-500 truncate"><i class="fa-solid fa-user mr-1 text-gray-400"></i>{{ lead.contact }}</p>
                <p v-if="lead.lossReason" class="text-xs text-red-400 mt-1 truncate"><i class="fa-solid fa-circle-info mr-1"></i>{{ lead.lossReason }}</p>
                <div v-if="lead.assignedTo" class="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-400">
                  <i class="fa-solid fa-user-check mr-1"></i>{{ lead.assignedTo.name }}
                </div>
              </div>
            </draggable>
          </div>
        </div>

      </div>

      <!-- Reminders Sidebar (Desktop) with Today/Tomorrow/All -->
      <aside class="hidden lg:block w-72 flex-shrink-0 border-l border-gray-200 bg-white">
        <div class="sticky top-0 h-screen overflow-y-auto p-4">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-xs font-bold text-gray-500 uppercase tracking-wider">Reminders</h3>
          </div>
          <!-- Today/Tomorrow/All toggle -->
          <div class="flex items-center gap-1 mb-3">
            <button @click="reminderFilter = 'today'" class="flex-1 py-1.5 rounded-lg text-xs font-bold transition-all" :class="reminderFilter === 'today' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'">Today</button>
            <button @click="reminderFilter = 'tomorrow'" class="flex-1 py-1.5 rounded-lg text-xs font-bold transition-all" :class="reminderFilter === 'tomorrow' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'">Tomorrow</button>
            <button @click="reminderFilter = 'all'" class="flex-1 py-1.5 rounded-lg text-xs font-bold transition-all" :class="reminderFilter === 'all' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'">All</button>
          </div>
          <div v-if="filteredReminders.length" class="space-y-2">
            <div v-for="reminder in filteredReminders" :key="reminder.id" @click="openReminderLead(reminder)" class="rounded-lg border p-2.5 text-xs cursor-pointer transition-all hover:shadow-md" :class="isPastDate(reminder.date) ? 'border-red-300 bg-red-50 overdue-reminder-pulse' : 'border-gray-200 hover:bg-gray-50'">
              <div class="flex items-start justify-between">
                <div class="min-w-0 flex-1">
                  <p class="font-semibold text-gray-900 truncate">{{ reminder.subject }}</p>
                  <p class="text-gray-500 mt-0.5 truncate">{{ reminder.lead ? reminder.lead.contact : reminder.leadName }}</p>
                  <p class="mt-1" :class="isPastDate(reminder.date) ? 'text-red-600 font-semibold' : 'text-gray-400'"><span v-if="isPastDate(reminder.date)" class="inline-flex items-center text-[9px] font-bold bg-red-600 text-white px-1 py-0.5 rounded mr-1 uppercase">Overdue</span>{{ formatReminderDate(reminder.date) }}</p>
                </div>
                <button @click.stop="confirmRemoveReminder(reminder)" class="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 flex-shrink-0 ml-1" title="Remove">
                  <i class="fa-solid fa-xmark text-xs"></i>
                </button>
              </div>
            </div>
          </div>
          <div v-else class="py-8 text-center">
            <i class="fa-solid fa-beer-mug-empty text-yellow-500 text-2xl mb-2 block"></i>
            <p class="text-xs text-gray-400 font-medium">{{ reminderFilter === 'today' ? "You're clear for today!" : reminderFilter === 'tomorrow' ? 'Nothing tomorrow!' : 'No reminders!' }}</p>
          </div>
        </div>
      </aside>
    </div>

    <!-- ===================== TASK DETAIL MODAL ===================== -->
    <div v-if="showTaskDetail" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black bg-opacity-40" @click="closeTaskDetail"></div>
      <div class="absolute inset-0 sm:inset-4 md:inset-y-6 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:max-w-2xl md:w-full bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden z-10" style="max-height:100vh;">
        <!-- Modal Header -->
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div class="text-left">
            <h2 class="text-base font-bold text-gray-900">{{ taskDetailLead.customer ? taskDetailLead.customer.company : 'Lead Details' }}</h2>
            <p class="text-xs text-gray-400 mt-0.5">{{ taskDetailLead.projectName || 'View and manage this lead' }}</p>
          </div>
          <button @click="closeTaskDetail" class="w-11 h-11 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors flex-shrink-0">
            <i class="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>
        <!-- Modal Body (scrollable) -->
        <div class="flex-1 overflow-y-auto">
          <div class="px-5 py-4 space-y-5">
            <!-- Status / Urgency / Flames / Assigned -->
            <div class="space-y-3">
              <div class="grid grid-cols-2 gap-3">
                <div class="text-left">
                  <label class="text-[10px] font-semibold text-gray-400 uppercase mb-1 block">Status</label>
                  <select v-model="taskDetailLead.status" @change="handleStatusChange(taskDetailLead)" class="w-full rounded-xl text-base sm:text-sm font-semibold px-3 py-3 sm:py-2 border outline-none" :class="statusSelectClass(taskDetailLead.status)">
                    <option value="pending">Pending</option>
                    <option value="quoted">Quoted</option>
                    <option value="follow_up">Follow Up</option>
                    <option value="won">Won</option>
                    <option value="lost">Lost</option>
                  </select>
                </div>
                <div class="text-left">
                  <label class="text-[10px] font-semibold text-gray-400 uppercase mb-1 block">Urgency</label>
                  <select v-model="taskDetailLead.urgency" @change="saveFieldInline(taskDetailLead, 'urgency')" class="w-full rounded-xl text-base sm:text-sm font-semibold px-3 py-3 sm:py-2 border outline-none" :class="urgencyClass(taskDetailLead.urgency)">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div class="text-left">
                  <label class="text-[10px] font-semibold text-gray-400 uppercase mb-1 block">Hot Lead Score</label>
                  <div class="flex items-center gap-1.5 mt-1">
                    <button v-for="n in 5" :key="n" @click="setFlames(taskDetailLead, n)" @mouseover="flameHover = n" @mouseleave="flameHover = 0" class="p-0.5 transition-transform hover:scale-125">
                      <i class="fa-solid fa-fire text-xl" :style="flameStyleInteractive(n, taskDetailLead.flames || 0, flameHover)"></i>
                    </button>
                    <button v-if="taskDetailLead.flames" @click="setFlames(taskDetailLead, 0)" class="text-[10px] text-gray-400 hover:text-gray-600 ml-1">clear</button>
                  </div>
                  <p class="text-[10px] text-gray-400 mt-1">Click a flame to set score</p>
                </div>
                <div class="text-left">
                  <label class="text-[10px] font-semibold text-gray-400 uppercase mb-1 block">Assigned To</label>
                  <select v-model="taskDetailAssignedId" @change="updateAssignment(taskDetailLead)" class="w-full rounded-xl text-base sm:text-sm px-3 py-3 sm:py-2 border border-gray-200 outline-none bg-white">
                    <option value="">Unassigned</option>
                    <option v-for="u in adminUsers" :key="u.id" :value="u.id">{{ u.name }}</option>
                  </select>
                  <p class="text-[10px] text-gray-400 mt-1">Auto-assigned to you on create</p>
                </div>
              </div>
            </div>

            <!-- Contact Details -->
            <div class="bg-gray-50 rounded-xl p-4 text-left">
              <h4 class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Contact Information</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div class="sm:col-span-2" v-if="taskDetailLead.jobType">
                  <span class="text-[10px] text-gray-400 uppercase block">Job Type</span>
                  <p class="text-blue-600 font-semibold"><i class="fa-solid fa-briefcase mr-1.5 text-blue-400"></i>{{ taskDetailLead.jobType }}</p>
                </div>
                <div>
                  <span class="text-[10px] text-gray-400 uppercase block">Contact</span>
                  <p class="text-gray-800 font-medium">{{ taskDetailLead.contact || '--' }}</p>
                </div>
                <div>
                  <span class="text-[10px] text-gray-400 uppercase block">Phone</span>
                  <p><a v-if="taskDetailLead.phone" :href="'tel:'+taskDetailLead.phone" class="text-blue-600 font-medium hover:underline">{{ taskDetailLead.phone }}</a><span v-else class="text-gray-400">--</span></p>
                </div>
                <div>
                  <span class="text-[10px] text-gray-400 uppercase block">Email</span>
                  <p><a v-if="taskDetailLead.email" :href="'mailto:'+taskDetailLead.email" class="text-blue-600 font-medium hover:underline">{{ taskDetailLead.email }}</a><span v-else class="text-gray-400">--</span></p>
                </div>
                <div>
                  <span class="text-[10px] text-gray-400 uppercase block">Region</span>
                  <p class="text-gray-800">{{ taskDetailLead.region || '--' }}</p>
                </div>
                <div class="sm:col-span-2">
                  <span class="text-[10px] text-gray-400 uppercase block">Address</span>
                  <p class="text-gray-800 text-xs">{{ taskDetailLead.formattedAddress || '--' }}</p>
                </div>
                <div class="sm:col-span-2">
                  <span class="text-[10px] text-gray-400 uppercase block">Project Address</span>
                  <p class="text-gray-800 text-xs">{{ taskDetailLead.projectFormattedAddress || '--' }}</p>
                </div>
                <div>
                  <span class="text-[10px] text-gray-400 uppercase block">Approx M3</span>
                  <p class="text-gray-800 font-medium">{{ taskDetailLead.approxM3 || '--' }}</p>
                </div>
              </div>
            </div>

            <!-- Notes Section -->
            <div class="text-left">
              <div class="flex items-center justify-between mb-2">
                <h4 class="text-xs font-bold text-gray-500 uppercase tracking-wider">Notes</h4>
                <span class="text-[10px] text-gray-400">Track interactions and decisions</span>
              </div>
              <div class="space-y-2 mb-3 max-h-48 overflow-y-auto">
                <div v-for="note in taskNotes" :key="note.id" class="bg-gray-50 rounded-lg p-3 text-sm border border-gray-100">
                  <p class="text-gray-800 whitespace-pre-wrap text-left">{{ note.text }}</p>
                  <div class="flex items-center justify-between mt-2 text-[10px] text-gray-400">
                    <span><i class="fa-solid fa-user mr-1"></i>{{ note.createdByName || 'Unknown' }}</span>
                    <span>{{ note.created ? $moment(note.created.seconds ? note.created.seconds * 1000 : note.created).format('DD MMM YYYY, h:mm a') : '' }}</span>
                  </div>
                </div>
                <div v-if="!taskNotes.length" class="text-xs text-gray-400 py-3 text-center">No notes yet</div>
              </div>
              <div class="flex gap-2">
                <input v-model="newNoteText" type="text" @keyup.enter="addNote(taskDetailLead)" class="flex-1 text-base sm:text-sm border border-gray-200 rounded-xl px-4 py-3 sm:py-2 outline-none focus:border-primary" placeholder="Type a note and press Enter..." />
                <button @click="addNote(taskDetailLead)" :disabled="!newNoteText.trim()" class="px-5 py-3 sm:py-2 rounded-xl text-base sm:text-sm font-semibold bg-primary text-white hover:opacity-90 disabled:opacity-40 transition-colors flex-shrink-0">Add</button>
              </div>
            </div>

            <!-- Reminders Section -->
            <div class="text-left">
              <div class="flex items-center justify-between mb-2">
                <h4 class="text-xs font-bold text-gray-500 uppercase tracking-wider">Reminders</h4>
                <span class="text-[10px] text-gray-400">Set follow-up dates</span>
              </div>
              <div class="space-y-2 mb-3">
                <div v-for="rem in taskReminders" :key="rem.id" class="flex items-center justify-between rounded-lg border p-2.5" :class="isPastDate(rem.date) ? 'border-red-200 bg-red-50' : 'border-gray-200'">
                  <div class="text-left">
                    <p class="text-sm font-medium text-gray-800">{{ rem.subject }}</p>
                    <p class="text-xs mt-0.5" :class="isPastDate(rem.date) ? 'text-red-600 font-semibold' : 'text-gray-400'">{{ formatReminderDate(rem.date) }}</p>
                  </div>
                  <button @click="confirmRemoveReminder(rem)" class="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 flex-shrink-0">
                    <i class="fa-solid fa-xmark text-xs"></i>
                  </button>
                </div>
                <div v-if="!taskReminders.length" class="text-xs text-gray-400 py-2 text-center">No reminders set</div>
              </div>
              <div class="flex gap-2 items-end">
                <div class="flex-1">
                  <label class="text-[10px] text-gray-400 block mb-1 text-left">Date</label>
                  <flat-pickr v-model="inlineReminderDate" :config="dateConfig" class="text-base sm:text-sm w-full bg-gray-50 rounded-xl border border-gray-200 px-3 py-3 sm:py-2 outline-none" />
                </div>
                <div class="flex-1">
                  <label class="text-[10px] text-gray-400 block mb-1 text-left">Subject</label>
                  <input v-model="inlineReminderSubject" type="text" class="text-base sm:text-sm w-full bg-gray-50 rounded-xl border border-gray-200 px-3 py-3 sm:py-2 outline-none" placeholder="e.g. Follow up call" />
                </div>
                <button @click="addInlineReminder(taskDetailLead)" :disabled="!inlineReminderSubject.trim()" class="px-4 py-3 sm:py-2 rounded-xl text-base sm:text-sm font-semibold bg-yellow-500 text-white hover:bg-yellow-600 disabled:opacity-40 transition-colors whitespace-nowrap flex-shrink-0">
                  <i class="fa-solid fa-bell mr-1.5"></i>Set
                </button>
              </div>
            </div>

            <!-- Additional Info -->
            <div v-if="taskDetailLead.information" class="text-left">
              <h4 class="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Additional Information</h4>
              <p class="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 border border-gray-100 whitespace-pre-wrap">{{ taskDetailLead.information }}</p>
            </div>
          </div>
        </div>
        <!-- Modal Footer -->
        <div class="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div class="flex items-center gap-2">
            <button @click="openModal(taskDetailLead); closeTaskDetail()" class="px-4 py-2.5 sm:px-3 sm:py-2 rounded-xl text-base sm:text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">
              <i class="fa-solid fa-pen mr-1.5"></i>Edit
            </button>
            <button @click="openAddProjectModal(taskDetailLead); closeTaskDetail()" class="px-4 py-2.5 sm:px-3 sm:py-2 rounded-xl text-base sm:text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">
              <i class="fa-solid fa-folder-plus mr-1.5"></i>Project
            </button>
          </div>
          <button @click="confirmRemoveLead(taskDetailLead); closeTaskDetail()" class="px-4 py-2.5 sm:px-3 sm:py-2 rounded-xl text-base sm:text-sm font-medium text-red-600 hover:bg-red-50 transition-colors">
            <i class="fa-solid fa-trash mr-1.5"></i>Delete
          </button>
        </div>
      </div>
    </div>

    <!-- ===================== CREATE/EDIT LEAD MODAL ===================== -->
    <div v-if="showModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black bg-opacity-40" @click="closeModal"></div>
      <div class="absolute inset-0 sm:inset-4 md:inset-y-6 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:max-w-lg md:w-full bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden z-10" style="max-height:100vh;">
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div class="text-left">
            <h2 class="text-base font-bold text-gray-900">{{ editingLead ? 'Edit Lead' : 'New Lead' }}</h2>
            <p class="text-xs text-gray-400 mt-0.5">{{ editingLead ? 'Update lead details below' : 'Fill in details to create a new lead' }}</p>
          </div>
          <button @click="closeModal" class="w-11 h-11 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors flex-shrink-0">
            <i class="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>
        <div class="flex-1 overflow-y-auto">
          <div class="px-5 py-4 space-y-4 text-left">
            <div>
              <label class="text-xs font-semibold text-gray-600 mb-1 block">Client <span class="text-gray-400 font-normal">- Your company division</span></label>
              <select v-model="lead.client" class="w-full rounded-xl h-12 sm:h-10 bg-white border border-gray-200 outline-none px-3 text-base sm:text-sm text-gray-700">
                <option :value="null" disabled>Select client...</option>
                <option :value="c" v-for="c in clients" :key="c">{{ c }}</option>
              </select>
            </div>
            <div>
              <label class="text-xs font-semibold text-gray-600 mb-1 block">Customer <span class="text-gray-400 font-normal">- The company you're quoting</span></label>
              <div class="flex gap-2">
                <div class="flex-1" v-if="showNewClient">
                  <input v-model="newClient" type="text" class="w-full h-12 sm:h-10 rounded-xl border border-gray-200 px-3 text-base sm:text-sm outline-none" placeholder="New customer name" />
                </div>
                <div class="flex-1" v-if="!showNewClient">
                  <multiselect label="company" track-by="company" @search-change="searchCustomers" :loading="loadingSelect" v-model="lead.customer" :options="customers" :optionsLimit="300000" placeholder="Search customers..."></multiselect>
                </div>
                <button v-if="!showNewClient" @click="showNewClient = true" class="h-12 w-12 sm:h-10 sm:w-10 rounded-xl bg-blue-500 text-white flex items-center justify-center hover:bg-blue-600 flex-shrink-0" title="Add new customer"><i class="fa-solid fa-plus"></i></button>
                <button v-if="showNewClient" @click="showNewClient = false" class="h-12 w-12 sm:h-10 sm:w-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center hover:bg-red-200 flex-shrink-0"><i class="fa-solid fa-xmark"></i></button>
              </div>
            </div>
            <div>
              <label class="text-xs font-semibold text-gray-600 mb-1 block">Project Name <span class="text-gray-400 font-normal">- Short description of the work</span></label>
              <input v-model="lead.projectName" type="text" class="w-full h-12 sm:h-10 rounded-xl border border-gray-200 px-3 text-base sm:text-sm outline-none" placeholder="e.g. Warehouse slab pour" />
            </div>
            <div>
              <label class="text-xs font-semibold text-gray-600 mb-1 block">Job Type <span class="text-gray-400 font-normal">- Type of testing work</span></label>
              <select v-model="lead.jobType" class="w-full h-12 sm:h-10 rounded-xl border border-gray-200 px-3 text-base sm:text-sm outline-none bg-white">
                <option value="">Select job type...</option>
                <option value="Concrete Testing">Concrete Testing</option>
                <option value="Soil Testing">Soil Testing</option>
                <option value="Concrete Scanning & Coring">Concrete Scanning & Coring</option>
                <option value="Investigation Work">Investigation Work</option>
                <option value="Package">Package</option>
              </select>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-xs font-semibold text-gray-600 mb-1 block">Region</label>
                <select v-model="lead.region" class="w-full h-12 sm:h-10 rounded-xl border border-gray-200 px-3 text-base sm:text-sm outline-none bg-white">
                  <option value="">Select region...</option>
                  <option v-for="r in availableRegions" :key="r.id" :value="r.name">{{ r.name }}</option>
                </select>
              </div>
              <div>
                <label class="text-xs font-semibold text-gray-600 mb-1 block">Urgency</label>
                <select v-model="lead.urgency" class="w-full h-12 sm:h-10 rounded-xl border border-gray-200 px-3 text-base sm:text-sm outline-none bg-white">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div>
              <label class="text-xs font-semibold text-gray-600 mb-1 block">Hot Lead Score <span class="text-gray-400 font-normal">- Rate how hot this lead is</span></label>
              <div class="flex items-center gap-2 mt-1">
                <button v-for="n in 5" :key="n" @click="lead.flames = n" @mouseover="leadFlameHover = n" @mouseleave="leadFlameHover = 0" class="p-1 transition-transform hover:scale-125">
                  <i class="fa-solid fa-fire text-xl" :style="flameStyleInteractive(n, lead.flames || 0, leadFlameHover)"></i>
                </button>
                <button v-if="lead.flames" @click="lead.flames = 0" class="text-xs text-gray-400 hover:text-gray-600 ml-2">Clear</button>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-xs font-semibold text-gray-600 mb-1 block">Contact Name</label>
                <input v-model="lead.contact" type="text" class="w-full h-12 sm:h-10 rounded-xl border border-gray-200 px-3 text-base sm:text-sm outline-none" placeholder="John Smith" />
              </div>
              <div>
                <label class="text-xs font-semibold text-gray-600 mb-1 block">Phone</label>
                <input v-model="lead.phone" type="tel" class="w-full h-12 sm:h-10 rounded-xl border border-gray-200 px-3 text-base sm:text-sm outline-none" placeholder="04xx xxx xxx" />
              </div>
            </div>
            <div>
              <label class="text-xs font-semibold text-gray-600 mb-1 block">Email</label>
              <input v-model="lead.email" type="email" class="w-full h-12 sm:h-10 rounded-xl border border-gray-200 px-3 text-base sm:text-sm outline-none" placeholder="john@company.com" />
            </div>
            <div>
              <label class="text-xs font-semibold text-gray-600 mb-1 block">Address <span class="text-gray-400 font-normal">- Contact/company address</span></label>
              <vue-google-autocomplete :options="autocompleteOptions" id="map" v-model="lead.formattedAddress" country="au" ref="autocomplete" v-on:placechanged="getAddressData" classname="w-full h-10 rounded-lg border border-gray-200 px-3 text-sm outline-none" placeholder="Start typing an address..." />
            </div>
            <div>
              <label class="text-xs font-semibold text-gray-600 mb-1 block">Project Address <span class="text-gray-400 font-normal">- Job site location</span></label>
              <vue-google-autocomplete :options="autocompleteOptions" id="project-address-map" v-model="lead.projectFormattedAddress" country="au" ref="projectAddressAutocomplete" v-on:placechanged="getProjectAddressDataLead" classname="w-full h-10 rounded-lg border border-gray-200 px-3 text-sm outline-none" placeholder="Start typing project address..." />
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-xs font-semibold text-gray-600 mb-1 block">Quoted Date</label>
                <flat-pickr v-model="lead.quotedDate" :config="config" class="w-full bg-gray-50 h-10 rounded-lg border border-gray-200 px-3 text-sm" />
              </div>
              <div>
                <label class="text-xs font-semibold text-gray-600 mb-1 block">Est. Start</label>
                <flat-pickr v-model="lead.estimatedStartDate" :config="config" class="w-full bg-gray-50 h-10 rounded-lg border border-gray-200 px-3 text-sm" />
              </div>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-xs font-semibold text-gray-600 mb-1 block">Approx M3</label>
                <input v-model="lead.approxM3" type="number" class="w-full h-12 sm:h-10 rounded-xl border border-gray-200 px-3 text-base sm:text-sm outline-none" placeholder="0" />
              </div>
              <div>
                <label class="text-xs font-semibold text-gray-600 mb-1 block">Status</label>
                <select v-model="lead.status" class="w-full h-12 sm:h-10 rounded-xl border border-gray-200 px-3 text-base sm:text-sm outline-none bg-white">
                  <option value="pending">Pending</option>
                  <option value="quoted">Quoted</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
            </div>
            <div>
              <label class="text-xs font-semibold text-gray-600 mb-1 block">Assign To <span class="text-gray-400 font-normal">- Defaults to you</span></label>
              <select v-model="leadAssignedId" class="w-full h-12 sm:h-10 rounded-xl border border-gray-200 px-3 text-base sm:text-sm outline-none bg-white">
                <option value="">Select team member...</option>
                <option v-for="u in adminUsers" :key="u.id" :value="u.id">{{ u.name }}</option>
              </select>
            </div>
            <div>
              <label class="text-xs font-semibold text-gray-600 mb-1 block">Additional Information</label>
              <textarea v-model="lead.information" rows="3" class="w-full rounded-xl border border-gray-200 px-4 py-3 sm:py-2 text-base sm:text-sm outline-none resize-none" placeholder="Any extra details about this lead..."></textarea>
            </div>
            <label class="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" v-model="lead.completed" class="rounded border-gray-300" />
              Mark as completed
            </label>
          </div>
        </div>
        <div class="px-5 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0 flex gap-2">
          <button @click="saveLead" class="flex-1 h-12 sm:h-10 rounded-xl text-base sm:text-sm font-semibold bg-primary text-white hover:opacity-90 transition-colors">
            {{ editingLead ? 'Update Lead' : 'Create Lead' }}
          </button>
          <button @click="closeModal" class="px-4 h-12 sm:h-10 rounded-xl text-base sm:text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
        </div>
      </div>
    </div>

    <!-- ===================== REMINDER MODAL ===================== -->
    <div v-if="showReminderModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black bg-opacity-40" @click="closeReminderModal"></div>
      <div class="absolute inset-x-0 bottom-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-md sm:w-full bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden z-10">
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
          <div class="text-left">
            <h2 class="text-base font-bold text-gray-900">Set Reminder</h2>
            <p class="text-xs text-gray-400 mt-0.5">You'll be notified on the selected date</p>
          </div>
          <button @click="closeReminderModal" class="w-11 h-11 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-200"><i class="fa-solid fa-xmark text-lg"></i></button>
        </div>
        <div class="px-5 py-4 space-y-4 text-left">
          <div>
            <label class="text-xs font-semibold text-gray-600 mb-1 block">Reminder Date</label>
            <flat-pickr v-model="reminder.date" :config="dateConfig" class="w-full bg-gray-50 h-10 rounded-lg border border-gray-200 px-3 text-sm" />
          </div>
          <div>
            <label class="text-xs font-semibold text-gray-600 mb-1 block">Subject</label>
            <input v-model="reminder.subject" type="text" class="w-full h-12 sm:h-10 rounded-xl border border-gray-200 px-3 text-base sm:text-sm outline-none" placeholder="e.g. Follow up on quote" />
          </div>
        </div>
        <div class="px-5 py-3 border-t border-gray-200 bg-gray-50 flex gap-2">
          <button @click="saveReminder()" class="flex-1 h-12 sm:h-10 rounded-xl text-base sm:text-sm font-semibold bg-yellow-500 text-white hover:bg-yellow-600 transition-colors"><i class="fa-solid fa-bell mr-1.5"></i>Save Reminder</button>
          <button @click="closeReminderModal" class="px-4 h-12 sm:h-10 rounded-xl text-base sm:text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
        </div>
      </div>
    </div>

    <!-- ===================== ADD PROJECT MODAL ===================== -->
    <div v-if="showAddProjectModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black bg-opacity-40" @click="closeAddProjectModal"></div>
      <div class="absolute inset-0 sm:inset-4 md:inset-y-6 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:max-w-lg md:w-full bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden z-10" style="max-height:100vh;">
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div class="text-left">
            <h2 class="text-base font-bold text-gray-900">Convert to Project</h2>
            <p class="text-xs text-gray-400 mt-0.5">Link to an existing project or create a new one</p>
          </div>
          <button @click="closeAddProjectModal" class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200 flex-shrink-0"><i class="fa-solid fa-xmark text-lg"></i></button>
        </div>
        <div class="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-left">
          <div v-if="toggleviewProject">
            <label class="text-xs font-semibold text-gray-600 mb-1 block">Select existing project</label>
            <select v-model="selectedProjectId" class="w-full h-12 sm:h-10 rounded-xl border border-gray-200 px-3 text-base sm:text-sm outline-none bg-white">
              <option disabled value="">Choose a project...</option>
              <option v-for="project in filteredProjects" :key="project.id" :value="project.id">{{ project.projectName }}</option>
            </select>
            <button @click="navigateToProject" :disabled="!selectedProjectId" class="mt-3 w-full h-12 sm:h-10 rounded-xl text-base sm:text-sm font-semibold bg-secondary text-white hover:opacity-90 disabled:opacity-40 transition-colors">View Project</button>
            <div class="flex items-center gap-3 my-4"><div class="flex-1 h-px bg-gray-200"></div><span class="text-xs text-gray-400">OR</span><div class="flex-1 h-px bg-gray-200"></div></div>
            <button @click="toggleviewProject = false" class="w-full h-12 sm:h-10 rounded-xl text-base sm:text-sm font-semibold bg-primary text-white hover:opacity-90 transition-colors"><i class="fa-solid fa-plus mr-1.5"></i>Create New Project</button>
          </div>
          <div v-if="!toggleviewProject" class="space-y-4">
            <div><label class="text-xs font-semibold text-gray-600 mb-1 block">Client</label><select v-model="newProject.client" class="w-full h-12 sm:h-10 rounded-xl border border-gray-200 px-3 text-base sm:text-sm outline-none bg-white"><option :value="c" v-for="c in clients" :key="c">{{ c }}</option></select></div>
            <div><label class="text-xs font-semibold text-gray-600 mb-1 block">Customer</label><multiselect label="company" track-by="company" @search-change="searchCustomers" :loading="loadingSelect" v-model="newProject.customerId" :options="customers" :optionsLimit="300000" placeholder="Search customers..."></multiselect></div>
            <div><label class="text-xs font-semibold text-gray-600 mb-1 block">Project Name</label><input v-model="newProject.projectName" type="text" class="w-full h-12 sm:h-10 rounded-xl border border-gray-200 px-3 text-base sm:text-sm outline-none" /></div>
            <div><label class="text-xs font-semibold text-gray-600 mb-1 block">Address</label><vue-google-autocomplete :options="autocompleteOptions" id="project-map" v-model="newProject.formattedAddress" country="au" ref="projectAutocomplete" v-on:placechanged="getProjectAddressData" placeholder="Project Address" classname="w-full h-10 rounded-lg border border-gray-200 px-3 text-sm outline-none" /></div>
            <div class="grid grid-cols-2 gap-3">
              <div><label class="text-xs font-semibold text-gray-600 mb-1 block">Est. Start</label><flat-pickr v-model="newProject.estimatedStartDate" :config="config" class="w-full bg-gray-50 h-10 rounded-lg border border-gray-200 px-3 text-sm" /></div>
              <div><label class="text-xs font-semibold text-gray-600 mb-1 block">Est. End</label><flat-pickr v-model="newProject.estimatedEndDate" :config="config" class="w-full bg-gray-50 h-10 rounded-lg border border-gray-200 px-3 text-sm" /></div>
            </div>
            <div v-for="field in newProjectFields" :key="field.key"><label class="text-xs font-semibold text-gray-600 mb-1 block">{{ field.label }}</label><input v-model="newProject[field.key]" :type="field.type" class="w-full h-12 sm:h-10 rounded-xl border border-gray-200 px-3 text-base sm:text-sm outline-none" /></div>
          </div>
        </div>
        <div v-if="!toggleviewProject" class="px-5 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0 flex gap-2">
          <button @click="saveProject" class="flex-1 h-12 sm:h-10 rounded-xl text-base sm:text-sm font-semibold bg-primary text-white hover:opacity-90 transition-colors">Save Project</button>
          <button @click="closeAddProjectModal" class="px-4 h-12 sm:h-10 rounded-xl text-base sm:text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
        </div>
      </div>
    </div>

    <!-- ===================== LOSS REASON MODAL ===================== -->
    <div v-if="showLossReasonModal" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black bg-opacity-40" @click="showLossReasonModal = false"></div>
      <div class="absolute inset-x-0 bottom-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-md sm:w-full bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden z-10">
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
          <div class="text-left"><h2 class="text-base font-bold text-gray-900">Loss Reason</h2><p class="text-xs text-gray-400 mt-0.5">Record why this lead was lost</p></div>
          <button @click="showLossReasonModal = false" class="w-11 h-11 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-200"><i class="fa-solid fa-xmark text-lg"></i></button>
        </div>
        <div class="px-5 py-4 text-left"><textarea v-model="lossReason" rows="3" class="w-full rounded-xl border border-gray-200 px-4 py-3 sm:py-2 text-base sm:text-sm outline-none resize-none" placeholder="e.g. Went with a cheaper competitor"></textarea></div>
        <div class="px-5 py-3 border-t border-gray-200 bg-gray-50 flex gap-2">
          <button @click="saveLossReason" class="flex-1 h-12 sm:h-10 rounded-xl text-base sm:text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors">Save Reason</button>
          <button @click="showLossReasonModal = false" class="px-4 h-12 sm:h-10 rounded-xl text-base sm:text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
        </div>
      </div>
    </div>

    <!-- ===================== REMINDER POPUP MODAL ===================== -->
    <div v-if="showReminderPopup && todaysReminders.length" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black bg-opacity-40" @click="dismissReminderPopup"></div>
      <div class="absolute inset-x-0 bottom-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-md sm:w-full bg-white sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden z-10">
        <div class="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-yellow-50">
          <div class="flex items-center gap-2">
            <i class="fa-solid fa-bell text-yellow-500 text-lg"></i>
            <div class="text-left"><h2 class="text-base font-bold text-gray-900">Today's Reminders</h2><p class="text-xs text-gray-500 mt-0.5">{{ todaysReminders.length }} due</p></div>
          </div>
          <button @click="dismissReminderPopup" class="w-11 h-11 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-200"><i class="fa-solid fa-xmark text-lg"></i></button>
        </div>
        <div class="px-5 py-4 space-y-3 max-h-64 overflow-y-auto">
          <div v-for="rem in todaysReminders" :key="rem.id" @click="openReminderLead(rem); dismissReminderPopup();" class="flex items-start gap-3 bg-yellow-50 rounded-lg p-3 border border-yellow-200 cursor-pointer hover:bg-yellow-100 hover:shadow-sm transition-all">
            <i class="fa-solid fa-clock text-yellow-500 mt-0.5"></i>
            <div class="flex-1 min-w-0 text-left">
              <p class="text-sm font-semibold text-gray-900">{{ rem.subject }}</p>
              <p class="text-xs text-gray-500 mt-0.5">{{ rem.lead ? rem.lead.contact : rem.leadName }}</p>
            </div>
            <i class="fa-solid fa-arrow-right text-gray-300 mt-1 text-xs"></i>
          </div>
        </div>
        <div class="px-5 py-3 border-t border-gray-200 bg-gray-50">
          <button @click="dismissReminderPopup" class="w-full h-12 sm:h-10 rounded-xl text-base sm:text-sm font-semibold bg-primary text-white hover:opacity-90 transition-colors">Got it</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Outlook from "@/views/Outlook.vue";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import NavMenu from "@/components/headers/navMenu";
import { bus } from "../main";
import VueGoogleAutocomplete from "vue-google-autocomplete";
import draggable from "vuedraggable";
import {
  putFire,
  postFire,
  getJobsByProjectId,
  getFire,
  deleteFire,
  getLatestJob,
  getClients,
  getLeads,
  getCrmReminders,
  setFire,
  getProjectsByClient,
  getRegions,
  getCrmNotes,
  getAdminUsers,
  getUsers
} from "../firestore/utils";
import { setGeoPoint } from "../firebase";
const geofire = require("geofire-common");
import Multiselect from "vue-multiselect";
import { auth } from "../firebase";
import axios from "axios";
import VueApexCharts from "vue-apexcharts";

export default {
  components: {
    Multiselect,
    VueGoogleAutocomplete,
    flatPickr,
    Outlook,
    NavMenu,
    draggable,
    apexchart: VueApexCharts,
  },
  data() {
    return {
      currentView: "table",
      searchQuery: "",
      filterLeads: "all",
      filterRegion: "all",
      filterCustomer: "all",
      filterUrgency: "all",
      showCompleted: false,
      sortColumn: "customer",
      sortOrder: "asc",

      leads: [],
      reminders: [],
      clients: [],
      customers: [],
      availableRegions: [],
      adminUsers: [],
      projects: [],

      // Reminder filter
      reminderFilter: "today",

      // Charts
      showCharts: false,
      chartsLoading: false,

      // Flame hover
      flameHover: 0,
      leadFlameHover: 0,

      // Lead form
      showModal: false,
      showNewClient: false,
      newClient: "",
      editingLead: null,
      leadAssignedId: "",
      lead: this.getEmptyLead(),

      // Task detail
      showTaskDetail: false,
      taskDetailLead: {},
      taskNotes: [],
      taskReminders: [],
      newNoteText: "",
      taskDetailAssignedId: "",
      inlineReminderDate: new Date(),
      inlineReminderSubject: "",

      // Reminder modal
      showReminderModal: false,
      reminder: { leadId: null, leadName: "", date: new Date(), subject: "" },

      // Add project modal
      showAddProjectModal: false,
      toggleviewProject: true,
      selectedLead: null,
      selectedProjectId: "",
      newProject: { client: null, customerId: null, projectName: "", formattedAddress: "", address: null, estimatedStartDate: new Date(), estimatedEndDate: new Date(), active: true },
      newProjectFields: [
        { key: "poNumber", label: "PO Number", type: "text" },
        { key: "projectType", label: "Project Type", type: "text" },
        { key: "callFrequency", label: "Call Frequency", type: "text" },
        { key: "contact", label: "Contact", type: "text" },
        { key: "email", label: "Email", type: "email" },
        { key: "phone", label: "Phone", type: "text" },
        { key: "company", label: "Company", type: "text" },
      ],

      // Draggable board lists
      pendingLeadsDraggable: [],
      quotedLeadsDraggable: [],
      followUpLeadsDraggable: [],
      wonLeadsDraggable: [],
      lostLeadsDraggable: [],

      // Loss reason
      showLossReasonModal: false,
      lossReason: "",

      // Reminder popup
      showReminderPopup: false,
      todaysReminders: [],

      // Misc
      loading: true,
      loadingSelect: false,
      autocompleteOptions: { types: ["geocode"] },

      config: { enableTime: true, altFormat: "d-m-Y H:i", altInput: true, allowInput: false },
      dateConfig: { altFormat: "d-m-Y", altInput: true, allowInput: false },
    };
  },
  computed: {
    currentUser() { return this.$store.state.currentUser; },
    visibleLeads() { return this.showCompleted ? this.leads : this.leads.filter(l => !l.completed); },
    totalLeadCount() { return this.visibleLeads.length; },
    pendingLeadCount() { return this.visibleLeads.filter(l => l.status === "pending").length; },
    quotedLeadCount() { return this.visibleLeads.filter(l => l.status === "quoted").length; },
    followUpLeadCount() { return this.visibleLeads.filter(l => l.status === "follow_up").length; },
    wonLeadCount() { return this.visibleLeads.filter(l => l.status === "won").length; },
    lostLeadCount() { return this.visibleLeads.filter(l => l.status === "lost").length; },
    hotLeadCount() { return this.visibleLeads.filter(l => (l.flames || 0) >= 3).length; },
    totalMeters() { return this.sortedLeads.reduce((acc, l) => acc + (Number(l.approxM3) || 0), 0); },
    overdueReminderCount() { return this.reminders.filter(r => this.isPastDate(r.date)).length; },
    uniqueCustomers() {
      const set = new Set(this.leads.map(l => l.customer ? l.customer.company : null).filter(Boolean));
      return Array.from(set).sort();
    },
    sortedLeads() {
      let leads = this.leads;
      if (!this.showCompleted) leads = leads.filter(l => !l.completed);
      if (this.filterLeads !== "all") leads = leads.filter(l => l.status === this.filterLeads);
      if (this.filterRegion !== "all") leads = leads.filter(l => l.region === this.filterRegion);
      if (this.filterCustomer !== "all") leads = leads.filter(l => l.customer && l.customer.company === this.filterCustomer);
      if (this.filterUrgency !== "all") leads = leads.filter(l => (l.urgency || "low") === this.filterUrgency);
      {
        const urgencyOrder = { critical: 4, high: 3, medium: 2, low: 1 };
        leads = leads.slice().sort((a, b) => {
          // Overdue leads always float to top
          const aOD = this.hasOverdueReminder(a) ? 1 : 0;
          const bOD = this.hasOverdueReminder(b) ? 1 : 0;
          if (aOD !== bOD) return bOD - aOD;
          if (!this.sortColumn) return 0;
          let aV, bV;
          switch (this.sortColumn) {
            case "customer": aV = (a.customer ? a.customer.company : "").toLowerCase(); bV = (b.customer ? b.customer.company : "").toLowerCase(); break;
            case "jobType": aV = (a.jobType || "").toLowerCase(); bV = (b.jobType || "").toLowerCase(); break;
            case "contact": aV = (a.contact || "").toLowerCase(); bV = (b.contact || "").toLowerCase(); break;
            case "region": aV = (a.region || "").toLowerCase(); bV = (b.region || "").toLowerCase(); break;
            case "urgency": aV = urgencyOrder[a.urgency || "low"] || 1; bV = urgencyOrder[b.urgency || "low"] || 1; break;
            case "flames": aV = a.flames || 0; bV = b.flames || 0; break;
            case "assigned": aV = (a.assignedTo ? a.assignedTo.name : "").toLowerCase(); bV = (b.assignedTo ? b.assignedTo.name : "").toLowerCase(); break;
            case "status": aV = a.status || ""; bV = b.status || ""; break;
            case "startDate": aV = a.estimatedStartDate || ""; bV = b.estimatedStartDate || ""; break;
            case "m3": aV = Number(a.approxM3) || 0; bV = Number(b.approxM3) || 0; break;
            default: return 0;
          }
          if (aV < bV) return this.sortOrder === "asc" ? -1 : 1;
          if (aV > bV) return this.sortOrder === "asc" ? 1 : -1;
          return 0;
        });
      }
      return leads;
    },
    searchFilteredLeads() {
      let leads = this.sortedLeads;
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim();
        leads = leads.filter(l =>
          (l.contact && l.contact.toLowerCase().includes(q)) ||
          (l.customer && l.customer.company && l.customer.company.toLowerCase().includes(q)) ||
          (l.formattedAddress && l.formattedAddress.toLowerCase().includes(q)) ||
          (l.information && l.information.toLowerCase().includes(q)) ||
          (l.email && l.email.toLowerCase().includes(q)) ||
          (l.phone && l.phone.toLowerCase().includes(q)) ||
          (l.region && l.region.toLowerCase().includes(q)) ||
          (l.projectName && l.projectName.toLowerCase().includes(q)) ||
          (l.client && l.client.toLowerCase().includes(q)) ||
          (l.jobType && l.jobType.toLowerCase().includes(q))
        );
      }
      return leads;
    },
    pendingLeads() { return this.searchFilteredLeads.filter(l => l.status === "pending"); },
    quotedLeads() { return this.searchFilteredLeads.filter(l => l.status === "quoted"); },
    followUpLeads() { return this.searchFilteredLeads.filter(l => l.status === "follow_up"); },
    wonLeads() { return this.searchFilteredLeads.filter(l => l.status === "won"); },
    lostLeads() { return this.searchFilteredLeads.filter(l => l.status === "lost"); },
    groupedByCustomer() {
      const map = {};
      this.searchFilteredLeads.forEach(l => {
        const name = l.customer ? l.customer.company : "No Customer";
        if (!map[name]) map[name] = [];
        map[name].push(l);
      });
      return Object.keys(map).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())).map(k => ({ customerName: k, leads: map[k] }));
    },
    filteredProjects() { return this.projects; },
    filteredReminders() {
      const todayStr = this.$moment().format("YYYY-MM-DD");
      const tomorrowStr = this.$moment().add(1, 'day').format("YYYY-MM-DD");
      let rems = this.reminders.slice();
      if (this.reminderFilter === "today") {
        rems = rems.filter(r => {
          const d = this.normalizeReminderDate(r.date);
          return d <= todayStr;
        });
      } else if (this.reminderFilter === "tomorrow") {
        rems = rems.filter(r => {
          const d = this.normalizeReminderDate(r.date);
          return d === tomorrowStr;
        });
      }
      // Sort by date
      return rems.sort((a, b) => {
        const aD = this.normalizeReminderDate(a.date);
        const bD = this.normalizeReminderDate(b.date);
        return aD.localeCompare(bD);
      });
    },
    // Chart data
    donutSeries() { return [this.pendingLeadCount, this.quotedLeadCount, this.followUpLeadCount, this.wonLeadCount, this.lostLeadCount]; },
    donutOptions() {
      return {
        chart: { type: 'donut' },
        labels: ['Pending', 'Quoted', 'Follow Up', 'Won', 'Lost'],
        colors: ['#eab308', '#3b82f6', '#a855f7', '#16a34a', '#dc2626'],
        legend: { position: 'bottom', fontSize: '12px' },
        dataLabels: { enabled: true, style: { fontSize: '11px' } },
        plotOptions: { pie: { donut: { size: '60%' } } },
      };
    },
    areaSeries() {
      const months = {};
      this.leads.forEach(l => {
        const key = l.created ? this.$moment(l.created.seconds ? l.created.seconds * 1000 : l.created).format("MMM YY") : "Unknown";
        months[key] = (months[key] || 0) + 1;
      });
      const keys = Object.keys(months).slice(-12);
      return [{ name: 'Leads', data: keys.map(k => months[k] || 0) }];
    },
    areaOptions() {
      const months = {};
      this.leads.forEach(l => {
        const key = l.created ? this.$moment(l.created.seconds ? l.created.seconds * 1000 : l.created).format("MMM YY") : "Unknown";
        months[key] = (months[key] || 0) + 1;
      });
      const keys = Object.keys(months).slice(-12);
      return {
        chart: { type: 'area', toolbar: { show: false }, sparkline: { enabled: false } },
        xaxis: { categories: keys },
        colors: ['#3b82f6'],
        fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
        stroke: { curve: 'smooth', width: 2 },
        dataLabels: { enabled: false },
        grid: { borderColor: '#f3f4f6' },
      };
    },
  },
  watch: {
    searchFilteredLeads: {
      handler() { this.syncDraggableLists(); },
      deep: true,
    },
  },
  methods: {
    getEmptyLead() {
      return {
        client: "KCT", customer: null, projectName: "", projectType: "", poNumber: "",
        contact: "", quotedDate: new Date(), approxM3: "", status: "pending",
        phone: "", email: "", callFrequency: "", information: "",
        formattedAddress: "", address: null,
        projectFormattedAddress: "", projectAddress: null,
        region: "", urgency: "low", flames: 0,
        estimatedStartDate: new Date(), estimatedEndDate: new Date(),
        created: null, geohash: null, geopoint: null, completed: false,
        assignedTo: null, createdBy: null, jobType: "",
      };
    },
    setView(view) {
      this.currentView = view;
      if (view === 'trello') this.syncDraggableLists();
      if (this.currentUser && this.currentUser.id) {
        putFire({ collection: "users", doc: this.currentUser.id, body: { crmView: view } }).catch(() => {});
      }
    },
    syncDraggableLists() {
      this.pendingLeadsDraggable = this.searchFilteredLeads.filter(l => l.status === "pending");
      this.quotedLeadsDraggable = this.searchFilteredLeads.filter(l => l.status === "quoted");
      this.followUpLeadsDraggable = this.searchFilteredLeads.filter(l => l.status === "follow_up");
      this.wonLeadsDraggable = this.searchFilteredLeads.filter(l => l.status === "won");
      this.lostLeadsDraggable = this.searchFilteredLeads.filter(l => l.status === "lost");
    },
    async onDragEnd(evt, targetStatus) {
      // Find all leads in each draggable list and update their status if changed
      const updateList = async (list, status) => {
        for (const lead of list) {
          if (lead.status !== status) {
            const oldStatus = lead.status;
            lead.status = status;
            if (status === "lost") {
              this.openLossReasonModal(lead);
            } else {
              await putFire({ collection: "leads", doc: lead.id, body: { status } });
              if (status === "won") {
                this.openAddProjectModal(lead);
              }
            }
          }
        }
      };
      await updateList(this.pendingLeadsDraggable, "pending");
      await updateList(this.quotedLeadsDraggable, "quoted");
      await updateList(this.followUpLeadsDraggable, "follow_up");
      await updateList(this.wonLeadsDraggable, "won");
      await updateList(this.lostLeadsDraggable, "lost");
    },

    // Styling
    statusPillClass(status) {
      if (status === "pending") return "bg-yellow-100 text-yellow-700";
      if (status === "quoted") return "bg-blue-100 text-blue-700";
      if (status === "follow_up") return "bg-purple-100 text-purple-700";
      if (status === "won") return "bg-green-100 text-green-700";
      if (status === "lost") return "bg-red-100 text-red-700";
      return "bg-gray-100 text-gray-600";
    },
    statusSelectClass(status) {
      if (status === "pending") return "border-yellow-300 bg-yellow-50 text-yellow-700";
      if (status === "quoted") return "border-blue-300 bg-blue-50 text-blue-700";
      if (status === "follow_up") return "border-purple-300 bg-purple-50 text-purple-700";
      if (status === "won") return "border-green-300 bg-green-50 text-green-700";
      if (status === "lost") return "border-red-300 bg-red-50 text-red-700";
      return "";
    },
    urgencyClass(urgency) {
      if (urgency === "critical") return "bg-red-100 text-red-700";
      if (urgency === "high") return "bg-orange-100 text-orange-700";
      if (urgency === "medium") return "bg-yellow-100 text-yellow-700";
      return "bg-gray-100 text-gray-500";
    },
    // Flame gradient: 1=yellow, 2=gold, 3=orange, 4=deep orange, 5=red
    flameStyle(n, current) {
      if (n > current) return { color: '#e5e7eb' };
      const colors = ['#facc15', '#f59e0b', '#f97316', '#ea580c', '#dc2626'];
      return { color: colors[n - 1] || '#e5e7eb' };
    },
    flameStyleInteractive(n, current, hover) {
      const active = hover > 0 ? hover : current;
      if (n > active) return { color: '#e5e7eb', transition: 'color 0.15s' };
      const colors = ['#facc15', '#f59e0b', '#f97316', '#ea580c', '#dc2626'];
      return { color: colors[n - 1] || '#e5e7eb', transition: 'color 0.15s' };
    },
    isPastDate(date) { return this.$moment(date).isBefore(this.$moment(), "day"); },
    hasOverdueReminder(lead) {
      const todayStr = this.$moment().format("YYYY-MM-DD");
      return this.reminders.some(r => r.leadId === lead.id && this.normalizeReminderDate(r.date) < todayStr);
    },
    normalizeReminderDate(date) {
      if (!date) return '';
      if (date.seconds) return new Date(date.seconds * 1000).toISOString().split('T')[0];
      if (typeof date === 'string') return date.split('T')[0];
      if (date instanceof Date) return date.toISOString().split('T')[0];
      return this.$moment(date).format('YYYY-MM-DD');
    },
    formatReminderDate(date) {
      if (!date) return '';
      return this.$moment(date.seconds ? date.seconds * 1000 : date).format('ddd, DD MMM YYYY');
    },
    formatDate(date) {
      if (!date) return '--';
      const ms = date.seconds ? date.seconds * 1000 : (date._seconds ? date._seconds * 1000 : new Date(date).getTime());
      if (isNaN(ms)) return '--';
      return this.$moment(ms).format('DD MMM YYYY');
    },
    setSort(column) {
      if (this.sortColumn === column) this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      else { this.sortColumn = column; this.sortOrder = "asc"; }
    },

    // Charts
    toggleCharts() {
      if (!this.showCharts) {
        this.showCharts = true;
        this.chartsLoading = true;
        setTimeout(() => { this.chartsLoading = false; }, 600);
      } else {
        this.showCharts = false;
      }
    },

    // Task Detail
    openReminderLead(reminder) {
      const lead = reminder.lead || this.leads.find(l => l.id === reminder.leadId);
      if (lead) this.openTaskDetail(lead);
    },
    async openTaskDetail(lead) {
      this.taskDetailLead = lead;
      this.taskDetailAssignedId = lead.assignedTo ? lead.assignedTo.id : "";
      this.flameHover = 0;
      this.showTaskDetail = true;
      this.newNoteText = "";
      this.inlineReminderSubject = "";
      this.inlineReminderDate = new Date();
      try { this.taskNotes = await getCrmNotes(lead.id); } catch { this.taskNotes = []; }
      this.taskReminders = this.reminders.filter(r => r.leadId === lead.id);
    },
    closeTaskDetail() {
      this.showTaskDetail = false;
      this.taskDetailLead = {};
      this.taskNotes = [];
      this.taskReminders = [];
      this.flameHover = 0;
    },
    async addNote(lead) {
      if (!this.newNoteText.trim()) return;
      const note = {
        leadId: lead.id, customerId: lead.customer ? lead.customer.id : null,
        text: this.newNoteText.trim(), createdByName: this.currentUser ? this.currentUser.name : "Unknown",
        createdById: this.currentUser ? this.currentUser.id : null, isClientNote: false, created: new Date(),
      };
      const result = await postFire({ collection: "crmNotes", body: note });
      this.taskNotes.unshift({ ...note, id: result.id });
      this.newNoteText = "";
      const newFlames = Math.min((lead.flames || 0) + 1, 5);
      if (newFlames !== lead.flames) {
        lead.flames = newFlames;
        await putFire({ collection: "leads", doc: lead.id, body: { flames: newFlames } });
      }
    },
    async addInlineReminder(lead) {
      if (!this.inlineReminderSubject.trim()) return;
      const payload = {
        leadId: lead.id, leadName: lead.projectName || (lead.customer ? lead.customer.company : ""),
        lead: lead, date: this.inlineReminderDate, subject: this.inlineReminderSubject.trim(),
        created: new Date(),
        createdBy: { id: this.currentUser ? this.currentUser.id : null, name: this.currentUser ? this.currentUser.name : null },
        assignedTo: lead.assignedTo || { id: this.currentUser ? this.currentUser.id : null, name: this.currentUser ? this.currentUser.name : null },
      };
      const result = await postFire({ collection: "crmReminders", body: payload });
      this.taskReminders.push({ ...payload, id: result.id });
      this.reminders.push({ ...payload, id: result.id });
      this.inlineReminderSubject = "";
      this.inlineReminderDate = new Date();
    },
    async setFlames(lead, n) {
      lead.flames = n;
      this.$forceUpdate();
      await putFire({ collection: "leads", doc: lead.id, body: { flames: n } });
    },
    async saveFieldInline(lead, field) {
      await putFire({ collection: "leads", doc: lead.id, body: { [field]: lead[field] } });
    },
    async updateAssignment(lead) {
      const user = this.adminUsers.find(u => u.id === this.taskDetailAssignedId);
      const assignedTo = user ? { id: user.id, name: user.name } : null;
      lead.assignedTo = assignedTo;
      await putFire({ collection: "leads", doc: lead.id, body: { assignedTo } });
    },

    // Lead CRUD
    openModal(lead) {
      this.showNewClient = false;
      this.newClient = "";
      this.leadFlameHover = 0;
      if (lead) {
        this.editingLead = lead;
        this.lead = { ...lead };
        this.leadAssignedId = lead.assignedTo ? lead.assignedTo.id : "";
      } else {
        this.editingLead = null;
        this.lead = this.getEmptyLead();
        this.leadAssignedId = this.currentUser ? this.currentUser.id : "";
      }
      this.showModal = true;
    },
    closeModal() { this.showModal = false; },
    async saveLead() {
      if (this.newClient) {
        const cust = await postFire({ collection: "customers", body: { company: this.newClient } });
        this.lead.customer = { id: cust.id, company: this.newClient };
        this.newClient = "";
        this.showNewClient = false;
      }
      const assignedUser = this.adminUsers.find(u => u.id === this.leadAssignedId);
      this.lead.assignedTo = assignedUser ? { id: assignedUser.id, name: assignedUser.name } : null;
      if (!this.editingLead) {
        this.lead.createdBy = this.currentUser ? { id: this.currentUser.id, name: this.currentUser.name } : null;
      }
      if (this.editingLead) {
        await putFire({ collection: "leads", doc: this.editingLead.id, body: { ...this.lead } });
      } else {
        await postFire({ collection: "leads", body: { ...this.lead, created: new Date(), completed: false } });
      }
      this.closeModal();
      this.getAllLeads();
    },

    // Status
    async handleStatusChange(lead) {
      if (lead.status === "won") { this.openAddProjectModal(lead); }
      else if (lead.status === "lost") { this.openLossReasonModal(lead); }
      else { await putFire({ collection: "leads", doc: lead.id, body: { status: lead.status } }); }
      this.syncDraggableLists();
    },
    openLossReasonModal(lead) { this.selectedLead = lead; this.showLossReasonModal = true; },
    async saveLossReason() {
      if (this.lossReason) {
        await putFire({ collection: "leads", doc: this.selectedLead.id, body: { status: "lost", lossReason: this.lossReason } });
        const idx = this.leads.findIndex(l => l.id === this.selectedLead.id);
        if (idx !== -1) { this.leads[idx].status = "lost"; this.leads[idx].lossReason = this.lossReason; }
        this.showLossReasonModal = false;
        this.lossReason = "";
      }
    },

    // Reminders
    openReminderModal(lead) {
      this.reminder = { leadId: lead.id, lead: lead, leadName: lead.projectName || (lead.customer ? lead.customer.company : ""), date: new Date(), subject: "" };
      this.showReminderModal = true;
    },
    closeReminderModal() { this.showReminderModal = false; },
    async saveReminder() {
      const payload = {
        ...this.reminder, created: new Date(),
        createdBy: { id: this.currentUser ? this.currentUser.id : null, name: this.currentUser ? this.currentUser.name : null },
        assignedTo: this.reminder.lead && this.reminder.lead.assignedTo ? this.reminder.lead.assignedTo : { id: this.currentUser ? this.currentUser.id : null, name: this.currentUser ? this.currentUser.name : null },
      };
      await postFire({ collection: "crmReminders", body: payload });
      try {
        const _user = await this.getCurrentUser();
        const userDoc = await getFire({ collection: "users", doc: _user.uid });
        if (userDoc.outlookToken) this.createEventCalender(userDoc.outlookToken, this.reminder);
      } catch {}
      this.closeReminderModal();
      this.getAllReminders();
    },
    async confirmRemoveReminder(reminder) {
      if (confirm('Remove reminder "' + reminder.subject + '"?')) {
        await deleteFire({ collection: "crmReminders", doc: reminder.id });
        this.reminders = this.reminders.filter(r => r.id !== reminder.id);
        this.taskReminders = this.taskReminders.filter(r => r.id !== reminder.id);
      }
    },
    dismissReminderPopup() { this.showReminderPopup = false; },

    async confirmRemoveLead(lead) {
      if (confirm('Delete lead "' + (lead.projectName || (lead.customer ? lead.customer.company : '')) + '"?')) {
        await deleteFire({ collection: "leads", doc: lead.id });
        this.leads = this.leads.filter(l => l.id !== lead.id);
      }
    },

    // Project
    async openAddProjectModal(lead) {
      this.selectedLead = lead;
      this.toggleviewProject = true;
      this.selectedProjectId = "";
      this.newProject = {
        client: lead.client, customerId: lead.customer, projectName: lead.projectName,
        formattedAddress: lead.projectFormattedAddress || lead.formattedAddress || "", address: lead.projectAddress || lead.address || null,
        region: lead.region || "", estimatedStartDate: new Date(), estimatedEndDate: new Date(),
        poNumber: lead.poNumber || "", projectType: lead.projectType || "",
        callFrequency: lead.callFrequency || "", contact: lead.contact || "",
        email: lead.email || "", phone: lead.phone || "", company: lead.customer ? lead.customer.company : "", active: true,
      };
      try { this.projects = await getProjectsByClient({ customerId: lead.customer ? lead.customer.id : null }); } catch { this.projects = []; }
      this.showAddProjectModal = true;
    },
    closeAddProjectModal() { this.showAddProjectModal = false; this.toggleviewProject = true; },
    getProjectAddressData(addressData) {
      this.newProject.address = addressData;
      this.newProject.formattedAddress = addressData.formatted_address;
    },
    async navigateToProject() {
      if (this.selectedProjectId) {
        const jobs = await getJobsByProjectId(this.selectedProjectId);
        if (jobs.length) this.$router.push({ name: "Jobs", query: { jobId: jobs[0].id } });
      }
    },
    async saveProject() {
      const latestJob = await getLatestJob();
      const newP = latestJob.projectId + 1;
      await setFire({
        collection: "projects", doc: newP.toString(),
        body: { ...this.newProject, active: true, created: new Date(), customerId: this.selectedLead.customer ? this.selectedLead.customer.id : null },
      });
      await this.saveEmptyJob(this.newProject, newP);
    },
    async saveEmptyJob(lead, newP) {
      if (typeof newP === "string") { newP = Number(newP); if (isNaN(newP)) return; }
      const payload = {
        formattedAddress: this.selectedLead.projectFormattedAddress || this.selectedLead.formattedAddress || "", client: lead.client || "",
        active: true, company: lead.company || "", phone: lead.phone || "",
        email: lead.email || "", callFrequency: lead.callFrequency || "",
        contact: lead.contact || "", quotedDate: lead.quotedDate || "",
        poNumber: lead.poNumber || "", region: lead.region || "",
        projectType: lead.projectType || "", estimatedStartDate: "", estimatedEndDate: "",
        customerId: this.selectedLead.customer ? this.selectedLead.customer.id : "",
        address: this.selectedLead.projectAddress || this.selectedLead.address || null,
        projectId: newP, projectName: lead.projectName || "",
        geohash: this.selectedLead.projectGeohash || this.selectedLead.geohash || "", geopoint: this.selectedLead.projectGeopoint || this.selectedLead.geopoint || null,
        status: "ON-CALL", contractor: null, meters: this.selectedLead.approxM3,
        mixCode: "", accepted: false, requirements: "", pourLocation: "",
        notesForTester: "", notes: this.selectedLead.information || "", updated: new Date(),
      };
      const _job = await postFire({ collection: "jobs", body: payload });
      this.$router.push({ name: "Jobs", query: { jobId: _job.id } });
    },

    // CSV
    exportLeadsCsv() {
      const rows = this.searchFilteredLeads;
      if (!rows.length) return;
      const header = "Created,Customer,Job Type,Contact,Phone,Email,Address,Region,Urgency,Flames,Meters,Status,Notes";
      const lines = rows.map(r => {
        const created = r.created ? this.$moment(r.created.seconds * 1000).format("DD-MMM-YYYY") : "";
        const customer = r.customer ? r.customer.company : "";
        return '"' + created + '","' + customer + '","' + (r.jobType || '') + '","' + (r.contact || '') + '","' + (r.phone || '') + '","' + (r.email || '') + '","' + (r.formattedAddress || '') + '","' + (r.region || '') + '","' + (r.urgency || 'low') + '",' + (r.flames || 0) + ',' + (r.approxM3 || 0) + ',"' + (r.status || '') + '","' + ((r.information || '').replace(/"/g, '""')) + '"';
      });
      const csv = [header, ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "crm-leads-" + this.$moment().format("YYYY-MM-DD") + ".csv";
      a.click(); URL.revokeObjectURL(url);
    },

    // Helpers
    getAddressData(addressData) {
      this.lead.address = addressData;
      this.lead.geohash = geofire.geohashForLocation([addressData.latitude, addressData.longitude]);
      this.lead.geopoint = setGeoPoint({ latitude: addressData.latitude, longitude: addressData.longitude });
      if (addressData.route && addressData.street_number) {
        this.lead.formattedAddress = addressData.street_number + ' ' + addressData.route + ', ' + addressData.locality + ', ' + addressData.administrative_area_level_1 + ' ' + addressData.country;
      }
      // Auto-populate project address if empty
      if (!this.lead.projectFormattedAddress) {
        this.lead.projectAddress = addressData;
        this.lead.projectFormattedAddress = this.lead.formattedAddress;
      }
    },
    getProjectAddressDataLead(addressData) {
      this.lead.projectAddress = addressData;
      if (addressData.latitude && addressData.longitude) {
        this.lead.projectGeohash = geofire.geohashForLocation([addressData.latitude, addressData.longitude]);
        this.lead.projectGeopoint = setGeoPoint({ latitude: addressData.latitude, longitude: addressData.longitude });
      }
      if (addressData.route && addressData.street_number) {
        this.lead.projectFormattedAddress = addressData.street_number + ' ' + addressData.route + ', ' + addressData.locality + ', ' + addressData.administrative_area_level_1 + ' ' + addressData.country;
      } else if (addressData.formatted_address) {
        this.lead.projectFormattedAddress = addressData.formatted_address;
      }
    },
    searchCustomers() {
      this.loadingSelect = true;
      this.axios.get("https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/search", {
        params: { indexName: "customers", filter: null, search: this.search, twilio: null },
      }).then(res => { this.customers = res.data; this.loadingSelect = false; })
        .catch(() => { this.loadingSelect = false; });
    },
    async getCurrentUser() {
      return new Promise((resolve, reject) => {
        const unsub = auth.onAuthStateChanged(user => { unsub(); user ? resolve(user) : reject(new Error("No user")); });
      });
    },
    async createEventCalender(accessToken, reminder) {
      const formattedDate = reminder.date instanceof Date ? reminder.date.toISOString() : new Date(reminder.date).toISOString();
      if (!formattedDate) return;
      try {
        await axios.post("https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets/calendar/event", {
          subject: reminder.subject, start: formattedDate, end: formattedDate,
          reminderMinutesBeforeStart: 15, accessToken,
        });
      } catch (error) { console.error("Error creating event:", error.message); }
    },
    loadGoogleMapsScript() {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) resolve();
        else {
          const script = document.createElement("script");
          script.src = "https://maps.googleapis.com/maps/api/js?key=AIzaSyAgzzzBwnKAnilSbETwkny6ApTz18jlHr4&libraries=places";
          script.async = true; script.defer = true;
          script.onload = () => resolve();
          script.onerror = () => reject("Failed to load Google Maps script.");
          document.head.appendChild(script);
        }
      });
    },

    // Data
    async getAllLeads() {
      this.leads = await getLeads();
      this.syncDraggableLists();
    },
    async getAllClients() { this.clients = await getClients(); },
    async getAllReminders() { this.reminders = await getCrmReminders(); },
    async fetchRegions() { try { this.availableRegions = await getRegions(); } catch { this.availableRegions = []; } },
    async fetchAdminUsers() { try { this.adminUsers = await getUsers(); } catch { this.adminUsers = []; } },
    async checkTodaysReminders() {
      if (!this.currentUser) return;
      const todayStr = this.$moment().format("YYYY-MM-DD");
      // Only surface the reminder popup once per calendar day, per user
      const shownKey = `crmRemindersShown:${this.currentUser.id}`;
      if (localStorage.getItem(shownKey) === todayStr) return;
      this.todaysReminders = this.reminders.filter(r => {
        const rDate = this.normalizeReminderDate(r.date);
        const isForUser = (r.createdBy && r.createdBy.id === this.currentUser.id) || (r.assignedTo && r.assignedTo.id === this.currentUser.id);
        return isForUser && rDate <= todayStr && !r.dismissed;
      });
      if (this.todaysReminders.length) {
        this.showReminderPopup = true;
        localStorage.setItem(shownKey, todayStr);
      }
    },
  },
  async mounted() {
    try { await this.loadGoogleMapsScript(); this.loading = false; } catch (e) { console.error(e); }
  },
  async created() {
    if (this.currentUser && this.currentUser.crmView) this.currentView = this.currentUser.crmView;
    await Promise.all([
      this.getAllClients(),
      this.getAllLeads(),
      this.getAllReminders(),
      this.fetchRegions(),
      this.fetchAdminUsers(),
    ]);
    this.$nextTick(() => this.checkTodaysReminders());
  },
};
</script>
<style scoped>
@keyframes pulse-red-glow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
  50% { box-shadow: 0 0 8px 4px rgba(220, 38, 38, 0.15); }
}
.overdue-pulse {
  animation: pulse-red-glow 2s ease-in-out infinite;
  border-left: 3px solid #dc2626 !important;
}
.overdue-reminder-pulse {
  animation: pulse-red-glow 2s ease-in-out infinite;
}
</style>
